import { Injectable } from '@angular/core';
import { uploadData, remove, list } from 'aws-amplify/storage';
import { SiteConfigurationMetadata, CategoryImageMetadata } from '../models/site-configuration-metadata.interface';
import type { Schema } from '../../../amplify/data/resource';
import outputs from '../../../amplify_outputs.json';

type SiteConfigurationSchema = Schema['SiteConfiguration']['type'];

export interface StorageConfig {
  bucket: 'main' | 'activity-pictures' | 'company-logos' | 'location-pictures';
  path: string;
  fileName: string;
}

export interface UploadResult {
  key: string;
  url: string;
  success: boolean;
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class StorageService {

  constructor() { }

  /**
   * Upload a file to the specified storage bucket
   */
  async uploadFile(file: File, config: StorageConfig): Promise<UploadResult> {
    try {
      const key = `${config.path}/${config.fileName}`;
      
      const result = await uploadData({
        key: key,
        data: file,
        options: {
          accessLevel: 'guest'
        }
      }).result;

      return {
        key: result.key,
        url: this.buildGuestPublicUrl(result.key),
        success: true
      };
    } catch (error) {
      console.error('Error uploading file:', error);
      return {
        key: '',
        url: '',
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Upload an activity picture
   */
  async uploadActivityPicture(file: File, activityId: string, fileName?: string): Promise<UploadResult> {
    const uniqueFileName = fileName || this.generateFileName(file.name, 'activity');
    const key = `activity-images/${activityId}/${uniqueFileName}`;
    return this.uploadSiteConfigFile(file, key);
  }

  /**
   * Upload a company logo
   */
  async uploadCompanyLogo(file: File, companyId: string, fileName?: string): Promise<UploadResult> {
    const config: StorageConfig = {
      bucket: 'company-logos',
      path: `companies/${companyId}`,
      fileName: fileName || file.name
    };
    
    return this.uploadFile(file, config);
  }

  /**
   * Upload a location picture
   */
  async uploadLocationPicture(file: File, category: string, fileName?: string): Promise<UploadResult> {
    const config: StorageConfig = {
      bucket: 'location-pictures',
      path: `locations/${category}`,
      fileName: fileName || file.name
    };
    
    return this.uploadFile(file, config);
  }

  /**
   * Upload a site configuration image (logo, header, favicon, etc.)
   */
  async uploadSiteConfigImage(file: File, imageType: 'logo' | 'header' | 'favicon', location: string, fileName?: string): Promise<UploadResult> {
    const uniqueFileName = fileName || this.generateFileName(file.name, imageType);
    const key = `site-config/${location}/${imageType}/${uniqueFileName}`;
    return this.uploadSiteConfigFile(file, key);
  }

  /**
   * Upload the main hero image used on the home page.
   */
  async uploadSiteConfigHeroImage(file: File, location: string, fileName?: string): Promise<UploadResult> {
    const uniqueFileName = fileName || this.generateFileName(file.name, 'hero-image');
    const key = `site-config/${location}/main/hero/image/${uniqueFileName}`;
    return this.uploadSiteConfigFile(file, key);
  }

  /**
   * Upload the main hero video used on the home page.
   */
  async uploadSiteConfigHeroVideo(file: File, location: string, fileName?: string): Promise<UploadResult> {
    const uniqueFileName = fileName || this.generateFileName(file.name, 'hero-video');
    const key = `site-config/${location}/main/hero/video/${uniqueFileName}`;
    return this.uploadSiteConfigFile(file, key);
  }

  /**
   * Upload a category image used on the home page tiles.
   */
  async uploadSiteConfigCategoryImage(file: File, location: string, categoryName: string, fileName?: string): Promise<UploadResult> {
    const categorySlug = categoryName.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
    const uniqueFileName = fileName || this.generateFileName(file.name, `category-${categorySlug}`);
    const key = `site-config/${location}/main-categories/${categorySlug}/image/${uniqueFileName}`;
    return this.uploadSiteConfigFile(file, key);
  }

  /**
   * Upload a file into the configured storage with guest read access.
   * Expects `key` to include the storage prefix, e.g. `site-config/...`.
   */
  private async uploadSiteConfigFile(file: File, key: string): Promise<UploadResult> {
    try {
      const result = await uploadData({
        key,
        data: file,
        options: {
          accessLevel: 'guest'
        }
      }).result;

      return {
        key: result.key,
        url: this.buildGuestPublicUrl(result.key),
        success: true
      };
    } catch (error) {
      console.error('Error uploading site-config file:', error);
      return {
        key: '',
        url: '',
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Get the URL for a file
   */
  async getFileUrl(key: string): Promise<string> {
    return this.buildGuestPublicUrl(key);
  }

  /**
   * Stable, non-expiring URL for guest-readable objects (`public/*` prefix in S3).
   * Presigned URLs expire and break `<video>` / `<img>` in browsers when refresh fails.
   */
  buildGuestPublicUrl(key: string): string {
    const trimmed = key?.trim();
    if (!trimmed) {
      throw new Error('Storage key is required');
    }

    const storage = outputs.storage;
    const bucket = storage?.bucket_name;
    const region = storage?.aws_region ?? 'us-east-1';
    if (!bucket) {
      throw new Error('Storage bucket is not configured');
    }

    const objectPath = trimmed.startsWith('public/') ? trimmed : `public/${trimmed}`;
    const encodedPath = objectPath
      .split('/')
      .map((segment) => encodeURIComponent(segment))
      .join('/');

    return `https://${bucket}.s3.${region}.amazonaws.com/${encodedPath}`;
  }

  /**
   * Re-resolve presigned media URLs from stored keys. Persisted `*Url` fields expire;
   * keys remain valid and `getUrl` produces fresh URLs for guest-readable objects.
   */
  async refreshSiteConfigurationUrls(config: SiteConfigurationSchema): Promise<SiteConfigurationSchema> {
    const out = { ...config } as SiteConfigurationSchema;

    const refreshPair = async (
      key: string | null | undefined,
      currentUrl: string | null | undefined
    ): Promise<string | undefined> => {
      const k = key?.trim();
      if (k) {
        try {
          return await this.getFileUrl(k);
        } catch (e) {
          console.warn('Could not refresh storage URL for key', k, e);
        }
      }
      const cur = currentUrl?.trim();
      return cur || undefined;
    };

    const logoUrl = await refreshPair(out.logoKey, out.logoUrl);
    if (logoUrl !== undefined) {
      out.logoUrl = logoUrl;
    }
    const headerImageUrl = await refreshPair(out.headerImageKey, out.headerImageUrl);
    if (headerImageUrl !== undefined) {
      out.headerImageUrl = headerImageUrl;
    }
    const faviconUrl = await refreshPair(out.faviconKey, out.faviconUrl);
    if (faviconUrl !== undefined) {
      out.faviconUrl = faviconUrl;
    }

    const rawMeta = out.metadata;
    if (rawMeta != null && String(rawMeta).trim() !== '') {
      try {
        const meta = JSON.parse(rawMeta as string) as SiteConfigurationMetadata;
        const mainHero = meta.mainHero ?? {};
        const newHero = { ...mainHero };
        const heroImg = await refreshPair(mainHero.imageKey, mainHero.imageUrl);
        if (heroImg !== undefined) {
          newHero.imageUrl = heroImg;
        }
        const heroVid = await refreshPair(mainHero.videoKey, mainHero.videoUrl);
        if (heroVid !== undefined) {
          newHero.videoUrl = heroVid;
        }
        meta.mainHero = newHero;

        const cats: Record<string, CategoryImageMetadata> = { ...(meta.categoryImages ?? {}) };
        for (const [name, entry] of Object.entries(cats)) {
          const u = await refreshPair(entry?.imageKey, entry?.imageUrl);
          if (u !== undefined) {
            cats[name] = { ...entry, imageUrl: u };
          }
        }
        meta.categoryImages = cats;
        out.metadata = JSON.stringify(meta);
      } catch {
        // leave metadata string unchanged
      }
    }

    return out;
  }

  /**
   * Collect unique storage object keys associated with a site configuration record
   * (top-level keys plus hero and category keys in metadata).
   */
  collectSiteConfigStorageKeys(config: {
    metadata?: string | null | undefined;
    logoKey?: string | null | undefined;
    headerImageKey?: string | null | undefined;
    faviconKey?: string | null | undefined;
  }): string[] {
    const keys = new Set<string>();
    const add = (k?: string | null) => {
      const t = k?.trim();
      if (t) keys.add(t);
    };

    add(config.logoKey);
    add(config.headerImageKey);
    add(config.faviconKey);

    let meta: SiteConfigurationMetadata = {};
    const raw = config.metadata;
    if (raw != null && raw !== '') {
      try {
        meta =
          typeof raw === 'string'
            ? (JSON.parse(raw) as SiteConfigurationMetadata)
            : (raw as SiteConfigurationMetadata);
      } catch {
        meta = {};
      }
    }

    add(meta.mainHero?.imageKey);
    add(meta.mainHero?.videoKey);

    const cats = meta.categoryImages ?? {};
    for (const v of Object.values(cats)) {
      add(v?.imageKey);
    }

    return [...keys];
  }

  /**
   * Delete multiple storage objects by key. Returns keys that failed to delete.
   */
  async deleteStorageKeys(keys: string[]): Promise<string[]> {
    const unique = [...new Set(keys.map((k) => k?.trim()).filter((k): k is string => !!k))];
    const failed: string[] = [];
    for (const key of unique) {
      const ok = await this.deleteFile(key);
      if (!ok) {
        failed.push(key);
      }
    }
    return failed;
  }

  /**
   * Delete a file from storage
   */
  async deleteFile(key: string): Promise<boolean> {
    try {
      await remove({
        key: key,
        options: {
          accessLevel: 'guest'
        }
      });
      
      return true;
    } catch (error) {
      console.error('Error deleting file:', error);
      return false;
    }
  }

  /**
   * List files in a directory
   */
  async listFiles(path: string): Promise<string[]> {
    try {
      const result = await list({
        prefix: path,
        options: {
          accessLevel: 'guest'
        }
      });
      
      return result.items.map(item => item.key);
    } catch (error) {
      console.error('Error listing files:', error);
      return [];
    }
  }

  /**
   * Get activity pictures for a specific activity
   */
  async getActivityPictures(activityId: string): Promise<string[]> {
    return this.listFiles(`activities/${activityId}`);
  }

  /**
   * Get company logos for a specific company
   */
  async getCompanyLogos(companyId: string): Promise<string[]> {
    return this.listFiles(`companies/${companyId}`);
  }

  /**
   * Get location pictures for a specific category
   */
  async getLocationPictures(category: string): Promise<string[]> {
    return this.listFiles(`locations/${category}`);
  }

  /**
   * Generate a unique filename with timestamp
   */
  generateFileName(originalName: string, prefix?: string): string {
    const timestamp = new Date().getTime();
    const extension = originalName.split('.').pop();
    const name = originalName.split('.')[0].replace(/[^a-zA-Z0-9]/g, '-');
    
    if (prefix) {
      return `${prefix}-${name}-${timestamp}.${extension}`;
    }
    
    return `${name}-${timestamp}.${extension}`;
  }

  /**
   * Validate file type and size
   */
  validateFile(
    file: File,
    maxSizeMB: number = 5,
    allowedTypes: string[] = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']
  ): { valid: boolean; error?: string } {
    // Check file size
    if (file.size > maxSizeMB * 1024 * 1024) {
      return {
        valid: false,
        error: `File size must be less than ${maxSizeMB}MB`
      };
    }

    // Check file type
    const isAllowedType = allowedTypes.some((allowed) => {
      // Support "video/*" style entries
      if (allowed.endsWith('/*')) {
        const prefix = allowed.slice(0, -1); // keep trailing slash semantics
        return file.type.startsWith(prefix);
      }
      return file.type === allowed;
    });

    if (!isAllowedType) {
      const allowedDisplay = allowedTypes.join(', ');
      return {
        valid: false,
        error: `Invalid file type. Allowed: ${allowedDisplay}`
      };
    }

    return { valid: true };
  }
}
